import { Injectable, PLATFORM_ID, Signal, signal, OnDestroy, inject } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

import { fromEvent, Observable, of, Subject, debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';

import { noop, WindowRef } from '@fcom/core';
import { finShare } from '@fcom/rx';

import { Breakpoint } from '../../interfaces/breakpoint.interface';

type BreakpointList = Partial<Record<Breakpoint, string>>;

const BREAKPOINT_QUERIES: BreakpointList = Object.freeze({
  mobile: 'only screen and (max-width: 599px)',
  tablet: 'only screen and (min-width: 600px) and (max-width: 1023px)',
  tablet_down: 'only screen and (max-width: 1023px)',
  laptop_up: 'only screen and (min-width: 1024px)',
  small_desktop: 'only screen and (min-width: 1024px) and (max-width: 1599px)',
  large_desktop: 'only screen and (min-width: 1600px)',
});

const EMPTY_MQL: MediaQueryList = {
  matches: false,
  media: '',
  onchange: null,
  addEventListener: noop,
  removeEventListener: noop,
  addListener: noop,
  removeListener: noop,
  dispatchEvent: () => false,
};

export type BreakpointName = keyof typeof BREAKPOINT_QUERIES;
type BreakPointRegistry = Partial<Record<BreakpointName, BreakpointMatcher>>;
type BreakpointMatcher = {
  matcher: MediaQueryList;
  signal: Signal<boolean>;
  listener: (event: MediaQueryListEvent) => void;
};

const ssrBreakpointMatcher: BreakpointMatcher = {
  matcher: EMPTY_MQL,
  signal: signal(false).asReadonly(),
  listener: noop,
};

@Injectable({
  providedIn: 'root',
})
export class MediaQueryService implements OnDestroy {
  private windowRef = inject(WindowRef);
  private platform = inject(PLATFORM_ID);

  private resizeEvent$: Observable<Event>;
  private breakpointRegistry: BreakPointRegistry;
  private readonly fallbackBreakpointSignal: Signal<boolean> = signal(false).asReadonly();

  constructor() {
    if (isPlatformBrowser(this.platform)) {
      this.resizeEvent$ = fromEvent<Event>(this.windowRef.nativeWindow, 'resize').pipe(
        debounceTime(100),
        distinctUntilChanged(),
        finShare()
      );
    }

    this.breakpointRegistry = this.createBreakpointRegistry(BREAKPOINT_QUERIES, isPlatformServer(this.platform));
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platform)) {
      this.clearBreakpointListeners();
    }
  }

  isBreakpoint$(breakpoint: BreakpointName): Observable<boolean> {
    if (isPlatformServer(this.platform)) {
      return of(false);
    }

    return this.resizeEvent$.pipe(
      map(() => this.matchesMedia(BREAKPOINT_QUERIES[breakpoint])),
      startWith(this.matchesMedia(BREAKPOINT_QUERIES[breakpoint])),
      distinctUntilChanged(),
      finShare()
    );
  }

  /**
   * Returns a Signal that indicates whether the specified breakpoint matches the current viewport.
   * @param breakpoint The predefied media query breakpoint to check.
   */
  isBreakPoint(breakpoint: BreakpointName): Signal<boolean> {
    return this.breakpointRegistry[breakpoint]?.signal ?? this.fallbackBreakpointSignal;
  }

  getResizeEvent$(): Observable<Event> {
    if (isPlatformServer(this.platform)) {
      return new Subject();
    }

    return this.resizeEvent$;
  }

  private matchesMedia(mediaQuery: string): boolean {
    return this.windowRef.nativeWindow.matchMedia(mediaQuery).matches;
  }

  private registerBreakpointMatcher(query: string): BreakpointMatcher {
    const mediaQueryList = this.windowRef.nativeWindow.matchMedia(query);
    const breakPointSignal = signal(mediaQueryList.matches);

    const listener = (event: MediaQueryListEvent) => breakPointSignal.set(event.matches);

    mediaQueryList.addEventListener('change', listener);

    return { matcher: mediaQueryList, signal: breakPointSignal.asReadonly(), listener };
  }

  private createBreakpointRegistry(breakpoints: BreakpointList, isServer = false): BreakPointRegistry {
    return Object.keys(breakpoints).reduce<BreakPointRegistry>((registry, breakpoint) => {
      registry[breakpoint] = isServer ? ssrBreakpointMatcher : this.registerBreakpointMatcher(breakpoints[breakpoint]);
      return registry;
    }, {});
  }

  private clearBreakpointListeners(): void {
    Object.values(this.breakpointRegistry).forEach(({ matcher, listener }) =>
      matcher.removeEventListener('change', listener)
    );
    this.breakpointRegistry = {};
  }
}
